import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDevice } from "./context/DeviceContext";

const keywords = [
  "Acquisition", "Acquire", "Acquires", "Amalgamation", "Arrangement", "Merger",
  "Demerge", "De merge", "Demerger", "disinvestment",
  "Re-structuring", "Restructuring", "Offer For Sale",
  "Bonus", "split", "Buy back", "Buyback",
  "Rights issue", "Right issue",
  "Public offer", "public offering",
  "fund raise", "fund raising", "raise fund",
  "New Project", "new contract", "new order",
  "order received", "Awarding of order",
  "Bagging", "beg", "begs", "winning",
  "Expansion", "to invest", "settlement",
  "Raising of funds", "Rating", "clarification",
  "Partnership", "Joint venture", "Collaboration",
  "Capital expenditure", "capacity addition",
  "Strategic alliance", "Revenue growth",
  "Profit increase", "Earnings beat",
  "Record earnings", "Cost reduction",
  "Debt reduction", "Product launch",
  "New technology", "Innovation", "License",
  "Patent granted", "FDA approval",
  "Contract extension", "Market expansion",
  "Upgraded", "downgraded",
  "Buy recommendation", "Positive outlook",
  "Guidance raised", "Guidance",
  "Milestone achieved", "Supply agreement",
  "Exclusive agreement", "Patent filing",
  "Product approval", "Regulatory approval",
  "Secondary offering", "Share repurchase",
  "Equity infusion", "Capital raise",
  "Increase in volume", "Spurt in Volume",
  "Movement in price", "name change",
  "Demand Notice", "Incorporation",

  "margins", "margin", "policy", "deal",
  "jumps", "drops", "outperform",
  "rise", "fall", "jump", "Tariff", "down",
  "bags", "Results", "expands", "slumps",
  "Slip", "rose", "highest-ever",
  "record", "records", "high",
  "selling", "sell", "IPO", "listing",
  "downgrades", "upgrades", "reaffirmed",
  "surge", "muted", "climbs", "misses",
  "crashes", "crash", "grow", "raise",
  "Promoters", "Promoter", "plunge",
  "Dividend", "allots", "allot",
  "hike", "slump", "pledged", "pledge",
  "rises", "hikes", "growth", "raises",
  "FTA", "Upside", "downside",
  "Crashed", "valuation", "valuations",
  "gains", "Layoffs", "Layoff",
  "Rallies", "losses", "cautious",
  "outlook", "soar", "Plummet",
  "Bullish", "Bearish", "allegations",
  "gain", "commissions",

  "bypoll", "High Court", "Elections",
  "crisis", "crunch", "Supreme Court",
  "fraud", "Stake", "analysts",
  "Price Target", "NCLT", "SEBI",
  "DHRP", "delisting", "Upper Circuit",
  "Lower Circuit", "QIP"
];

function Announcements() {
  const { isMobile } = useDevice();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [marketCapFilter, setMarketCapFilter] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState(null);
  const [uniqueDates, setUniqueDates] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'an_dt', direction: 'desc' });

  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [showImportantKeywords, setShowImportantKeywords] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/announcements`)
      .then(res => {
        setData(res.data);
        setLoading(false);
        const dateSet = new Set(
          res.data
            .map(row => row.an_dt)
            .filter(Boolean)
            .map(d => {
              const dt = new Date(d);
              return dt.toLocaleDateString('en-CA');
            })
        );
        setUniqueDates(Array.from(dateSet).sort((a, b) => new Date(b) - new Date(a)));
      })
      .catch(err => {
        console.error('Failed to fetch announcements', err);
        setLoading(false);
      });
  }, []);

  const toggleExpanded = (index) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString('en-IN');
  };

  const escapedKeywords = useMemo(
    () =>
      keywords
        .map((k) => k.trim())
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
        .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
    []
  );

const getAnnouncementText = (row) =>
  `${row?.announcement_type ?? ''} ${row?.attchmntText ?? row?.desc ?? ''}`;

const getMatchedKeywordsForRow = (row) => {
  const text = getAnnouncementText(row);

  return keywords.filter((kw) => {
    const escaped = kw.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'i');
    return regex.test(text);
  });
};

const keywordCounts = useMemo(() => {
  const map = new Map();

  data.forEach((row) => {
    if (selectedDateFilter) {
      const rowDate = new Date(row.an_dt || row.sort_date || '');
      rowDate.setHours(0, 0, 0, 0);
      const localRowDate = rowDate.toLocaleDateString('en-CA');
      if (localRowDate !== selectedDateFilter) return;
    }

    getMatchedKeywordsForRow(row).forEach((kw) => {
      map.set(kw, (map.get(kw) || 0) + 1);
    });
  });

  return Array.from(map.entries())
    .map(([keyword, count]) => ({ keyword, count }))
    .sort((a, b) => b.count - a.count || a.keyword.localeCompare(b.keyword));
}, [data, selectedDateFilter]);

  const highlightText = (text = '') => {
    let result = text;  
    escapedKeywords.forEach((keyword) => {
      const regex = new RegExp(`\\b(${keyword})\\b`, 'gi');
      result = result.replace(regex, `<span class="bg-yellow-200 font-semibold">$1</span>`);
    });  
    return result;
  };

  const getChange = (curr, prev) => {
    if (isNaN(curr) || isNaN(prev) || prev === 0) return '—';
    const change = ((curr - prev) / prev) * 100;
    const colorClass = change >= 0 ? 'text-green-600' : 'text-red-600';
    return <span className={`font-semibold ${colorClass}`}>{change.toFixed(2)}%</span>;
  };

  const renderSortableHeader = (label, key, extraClass = '') => (
    <th
      className={`p-2 cursor-pointer select-none text-blue-600 ${extraClass}`}
      onClick={() =>
        setSortConfig(prev => ({
          key,
          direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }))
      }
    >
      {label}{sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const filteredData = data.filter((row) => {
    const marketCap = parseFloat(row.marketCap) || 0;
    const marketCapMatch = marketCap > 800 * 1e7;

    const rowDate = new Date(row.an_dt || row.sort_date || '');
    rowDate.setHours(0, 0, 0, 0);
    const localRowDate = rowDate.toLocaleDateString('en-CA');
    const isSameSelectedDate = selectedDateFilter
      ? localRowDate === selectedDateFilter
      : true;

    let keywordMatch = true;

    if (selectedKeyword) {
      const escaped = selectedKeyword
        .trim()
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const regex = new RegExp(`\\b${escaped}\\b`, 'i');

      keywordMatch = regex.test(getAnnouncementText(row));
    }

    return (
      keywordMatch &&
      (!marketCapFilter || marketCapMatch) &&
      isSameSelectedDate
    );
  });
  const enrichedData = filteredData.map(row => {
    const curr = parseFloat(row.currentPrice);
    const prev = parseFloat(row.previousClose);
    const change = !isNaN(curr) && !isNaN(prev) && prev !== 0 ? ((curr - prev) / prev) * 100 : null;
    return { ...row, change };
  });

  const sortedData = [...enrichedData].sort((a, b) => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    if (key === 'change') {
      const valA = isNaN(a.change) ? -Infinity : a.change;
      const valB = isNaN(b.change) ? -Infinity : b.change;
      return dir * (valA - valB);
    }
    const dateA = new Date(a.an_dt || a.sort_date);
    const dateB = new Date(b.an_dt || b.sort_date);
    return dir * (dateA - dateB);
  });

  return (
    <div className="p-4 mb-4">
      {loading ? (
        <div className="text-center text-blue-600 font-medium py-6 animate-pulse">Loading Data...</div>
      ) : (
        <div>
          {/* Filters */}
          <div className="mb-4">
            {!isMobile && (
              <div className="mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setShowImportantKeywords((v) => !v)}
                    className="px-3 py-1 text-xs rounded-full border bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    title="Show or hide important keywords"
                  >
                    Important keywords {showImportantKeywords ? "▲" : "▼"}
                  </button>

                  {selectedKeyword && (
                    <span className="text-xs text-gray-500">
                      Showing announcements for: {selectedKeyword}
                    </span>
                  )}
                </div>

                {showImportantKeywords && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setSelectedKeyword(null)}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        selectedKeyword === null
                          ? "bg-amber-500 text-white border-amber-500"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      All
                    </button>

                    {keywordCounts.map(({ keyword, count }) => (
                      <button
                        key={keyword}
                        onClick={() =>
                          setSelectedKeyword((prev) => (prev === keyword ? null : keyword))
                        }
                        className={`px-3 py-1 text-xs rounded-full border ${
                          selectedKeyword === keyword
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                        }`}
                      >
                        {keyword} ({count})
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Date filter */}
            {uniqueDates.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-gray-600 mr-1">Date:</span>
                {uniqueDates.map(dateStr => (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDateFilter(dateStr)}
                    className={`px-3 py-1 text-xs rounded-full border ${
                      selectedDateFilter === dateStr ? 'bg-yellow-300 font-semibold' : 'bg-gray-100'
                    }`}
                  >
                    {new Date(dateStr).toLocaleDateString('en-IN')}
                  </button>
                ))}
                {selectedDateFilter && (
                  <button
                    onClick={() => setSelectedDateFilter(null)}
                    className="px-2 py-1 text-xs border border-red-400 rounded-full bg-white text-red-600 ml-2"
                  >
                    Clear Date
                  </button>
                )}
              </div>
            )}

            <div className="text-sm mt-3 text-gray-600">Updates daily 11 PM, Total rows - {sortedData.length}</div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="table-auto border-collapse w-full text-sm text-gray-800 font-normal">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-2 text-left sticky left-0 bg-gray-200 z-50">Symbol</th>
                  {renderSortableHeader('Date', 'an_dt')}
                  {renderSortableHeader('Change', 'change')}
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Attachment</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-gray-500 py-4">No matching announcements found.</td>
                  </tr>
                ) : (
                  sortedData.map((row, idx) => {
                    const fullText = row.attchmntText || row.desc || '';
                    const isExpanded = expandedRows[idx];
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                        <td className="p-2 sticky left-0 bg-inherit z-10 font-medium">
                          <a href={`symbol/${row.symbol}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            {row.symbol || '—'}
                          </a>
                        </td>
                        <td className="p-2">{formatDate(row.an_dt || row.sort_date)}</td>
                        <td className="p-2">
                          {(() => {
                            const price = parseFloat(row.currentPrice);
                            const prev = parseFloat(row.previousClose);
                            if (isNaN(price) || isNaN(prev)) return "—";

                            const change = price - prev;
                            const pct = (change / prev) * 100;

                            const colorClass =
                              pct > 0 ? "text-green-600" : pct < 0 ? "text-red-600" : "text-gray-500";

                            return (
                              <div>
                                {/* percentage change */}
                                <div className={`text-sm font-bold ${colorClass}`}>
                                  {pct >= 0 ? "+" : ""}
                                  {pct.toFixed(2)}%
                                </div>

                                {/* ₹ price */}
                                <div className="text-xs text-gray-600">
                                  ₹{price.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-2">{row.announcement_type || row.desc || '—'}</td>
                        <td className="p-2">
                          <span
                            dangerouslySetInnerHTML={{
                              __html: isExpanded
                                ? highlightText(fullText)
                                : highlightText(fullText.slice(0, 250)) + (fullText.length > 250 ? '...' : '')
                            }}
                          />
                          {fullText.length > 200 && (
                            <button
                              className="ml-2 text-blue-600 underline text-xs"
                              onClick={() => toggleExpanded(idx)}
                            >
                              {isExpanded ? 'less' : 'more'}
                            </button>
                          )}
                        </td>
                        <td className="p-2">
                          {row.attchmntFile ? (
                            <a href={row.attchmntFile} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                              View PDF
                            </a>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Announcements;
